import { Box, styled } from '@mui/material';
import { useCompanyPlanContext } from 'FeatureModules/Auth/Contexts/CompanyPlanContext';
import Image from 'DesignSystem/DataDisplay/Image/Image';
import CardContainer from 'DesignSystem/Layout/CardContainer/CardContainer';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import ThemeTypography from 'DesignSystem/Primitives/Typography/ThemeTypography';
import React, { FC } from 'react';
import { Booleanish, Tbooleanish } from 'types/booleanish.types';
import { ESiteType, IBaseSite } from 'FeatureModules/Site/types/site.types';
import {
  getSiteSecondTitle,
  getSiteTitle,
  SiteTypeLabels,
} from 'FeatureModules/Site/utils/site.utils';
import FlexBox from 'Components/FlexBox';

interface IManageSiteSummaryProps {
  site: IBaseSite;
  requiredSiteType?: ESiteType;
  requiredRawMaterialTitle?: string;
  requiredLocationName: string;
  errors: TManageSiteErrors;
}

export type TManageSiteErrors = { siteType?: string; rawMaterial?: string; country?: string };

const StyledItem = styled('li')<{ error: Tbooleanish }>(({ theme, error }) => ({
  color: error === 'true' ? theme.custom.themeColors.error[100] : 'inherit',
}));

const StyledList = styled('ul')(({ theme }) => ({
  margin: 0,
  paddingLeft: 20,
}));

const ClusterContainer = styled(FlexBox)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.custom.themeColors.primary[5],
  borderRadius: 6,
  border: `1px solid ${theme.custom.themeColors.primary[40]}`,
  width: '100%',
  flexDirection: 'column',
}));

const ManageSiteSummary: FC<IManageSiteSummaryProps> = ({
  site,
  requiredSiteType,
  requiredRawMaterialTitle,
  requiredLocationName,
  errors,
}) => {
  const isCluster = site.siteType === ESiteType.CLUSTER;
  const { company } = useCompanyPlanContext();

  const siteSecondTitle = getSiteSecondTitle({
    site,
    farmDisplayOption: company.farmDisplayOption,
    siteDisplayOption: company.siteDisplayOption,
  });

  return (
    <ClusterContainer>
      <FlexBox alignSelf="flex-start" mb={0.5}>
        <ThemeTypography variant="BODY_MEDIUM_BOLD">
          {isCluster
            ? 'Select sites which belong to this cluster:'
            : 'Select which cluster this site should belong to:'}
        </ThemeTypography>
      </FlexBox>

      <FlexBox width="100%" gap={2} alignItems="flex-start">
        <CardContainer sx={{ flex: 0.5 }} hasShadow={false} padding={1.5} hasBorder>
          <FlexBox display="flex" alignItems="flex-start" gap={2}>
            {!!site.image ? (
              <Image width={44} height={44} image={site.image} />
            ) : (
              <Box display="flex" bgcolor="white" borderRadius="6px" padding="4px">
                <Icon
                  name={isCluster ? 'site-cluster-placeholder' : 'site-placeholder'}
                  size="xx-large"
                  color="blue-ice"
                />
              </Box>
            )}

            <FlexBox flexDirection="column" alignItems="flex-start" width="calc(100% - 56px)">
              <FlexBox gap={1} width="100%">
                <ThemeTypography variant="ITEM_TITLE" autoOverflow>
                  {getSiteTitle({
                    site,
                    farmDisplayOption: company.farmDisplayOption,
                    siteDisplayOption: company.siteDisplayOption,
                  })}
                </ThemeTypography>
                {siteSecondTitle && (
                  <>
                    <ThemeTypography variant="LABEL_INPUT" color="GRAY_40">
                      |
                    </ThemeTypography>
                    <ThemeTypography variant="LABEL_INPUT" color="GRAY_40" autoOverflow>
                      {siteSecondTitle}
                    </ThemeTypography>
                  </>
                )}
              </FlexBox>

              <FlexBox gap={1}>
                <FlexBox gap={0.5}>
                  <Icon name="tag-filled" size="small" color="gray-60" />
                  <ThemeTypography variant="LABEL_INPUT">
                    {site.siteType === ESiteType.CLUSTER && site.sitesType
                      ? `${SiteTypeLabels[site.sitesType]} cluster`
                      : SiteTypeLabels[site.siteType]}
                  </ThemeTypography>
                </FlexBox>

                {isCluster && (
                  <FlexBox gap={0.5}>
                    <Icon name="farm" size="medium" color="gray-60" />
                    <ThemeTypography variant="LABEL_INPUT">
                      {site.sitesCount}{' '}
                      {site.sitesType
                        ? `${SiteTypeLabels[site.sitesType].toLowerCase()}s`
                        : 'sites'}
                    </ThemeTypography>
                  </FlexBox>
                )}

                {site.siteType === ESiteType.FARM && (
                  <FlexBox gap={0.5}>
                    <Icon name="raw-material" size="medium" color="gray-60" />
                    <ThemeTypography variant="LABEL_INPUT">
                      {site.rawMaterial.title}
                    </ThemeTypography>
                  </FlexBox>
                )}

                <FlexBox gap={0.5}>
                  <Icon name="location" size="small" color="gray-60" />
                  <ThemeTypography variant="LABEL_INPUT">{site.locationName}</ThemeTypography>
                </FlexBox>
              </FlexBox>
            </FlexBox>
          </FlexBox>
        </CardContainer>

        <FlexBox flex={0.5} alignItems="flex-start" flexDirection="column">
          <ThemeTypography variant="BODY_SMALL" color="BLUE_100">
            Sites must share certain properties to be in the same cluster:
          </ThemeTypography>
          <StyledList>
            {requiredSiteType && (
              <StyledItem error={Booleanish(errors.siteType)}>
                <ThemeTypography variant="BODY_SMALL" color={errors.siteType ? 'RED' : 'BLUE_100'}>
                  <b>Site type:</b> {SiteTypeLabels[requiredSiteType]}
                </ThemeTypography>
              </StyledItem>
            )}
            <StyledItem error={Booleanish(errors.country)}>
              <ThemeTypography variant="BODY_SMALL" color={errors.country ? 'RED' : 'BLUE_100'}>
                <b>Location:</b> Within the same country ({requiredLocationName})
              </ThemeTypography>
            </StyledItem>
            {requiredSiteType === ESiteType.FARM && (
              <StyledItem error={Booleanish(errors.rawMaterial)}>
                <ThemeTypography
                  variant="BODY_SMALL"
                  color={errors.rawMaterial ? 'RED' : 'BLUE_100'}
                >
                  <b>Focus crop/material:</b> {requiredRawMaterialTitle}
                </ThemeTypography>
              </StyledItem>
            )}
          </StyledList>
        </FlexBox>
      </FlexBox>
    </ClusterContainer>
  );
};

export default ManageSiteSummary;
